import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import Badge from "../../../../../components/elements/Badge";
import { taxonomyDelete, taxonomyList, taxonomyStatusChange } from "../../../../../services/taxonomyService";
import DeleteModal from "../../../../../components/common/DeleteModal";
import CategoryMangeDrawer from "../../../../../components/sections/admin/Category/CategoryMangeDrawer";
import Table from "../../../../../components/elements/table/Table";
import Button from "../../../../../components/form/Button";
import { setTitle } from "../../../../../helpers/MetaTag";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { getPaginationRange, handleFilterChange } from "../../../../../helpers";
import TableControls from "../../../../../components/elements/TableControls";
import { featured_filter, status_filter } from "../../../../../components/constant/constant";
import ActionBar from "../../../../../components/elements/ActionBar";
import TagFilter from "../../../../../components/elements/TagFilter";

const MenuCategory = () => {
  setTitle("Menu Categories");
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const userLang = localStorage.getItem("lang") || "en";
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 100,
    page: 1,
    sortQuery: "ordering",
  });
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectFilterItem, setSelectFilterItem] = useState();
  const [selectedAllFilters, setSelectedAllFilters] = useState([]);

  const { data, isLoading, updateData, isFetching } = useQuery({
    queryKey: ["post-categories", filters, search, selectFilterItem],
    queryFn: async () => {
      const res = await taxonomyList({
        ...filters,
        ...(selectedFilter === "status" && { status: selectFilterItem?.value }),
        ...(selectedFilter === "featured" && { isFeatured: selectFilterItem?.value }),
        ...(search && { keyword: search }),
        type: "product-category",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  //  status change handler =================>

  const { mutate: statusChangeHandler, isPending: statusChangeInPending } = useMutation({
    mutationFn: async ({ id, status }) => {
      queryClient.setQueryData(["post-categories", filters, search, selectFilterItem], (state) => {
        if (!state) return;

        const updatedData = state?.data?.map((item) =>
          item?._id === id
            ? {
                ...item,
                status: item.status === "active" ? "inactive" : "active",
              }
            : item
        );

        return { ...state, data: updatedData };
      });
      const res = await taxonomyStatusChange({ ids: [id], status });

      return { res, payload: { id, status } };
    },

    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        toast.success("Category status change successfully.");
      } else {
        queryClient.setQueryData(["post-categories", filters, search, selectFilterItem], (state) => {
          if (!state) return;

          const revertedData = state?.data?.map((item) =>
            item?._id === payload.id
              ? {
                  ...item,
                  status: item.status === "active" ? "inactive" : "active",
                }
              : item
          );

          return { ...state, data: revertedData };
        });

        toast.error(res?.message || "Failed to update status.");
      }
    },

    onError: (error) => {
      toast.error(error?.message || "Something went wrong while updating status.");
      console.error("Status update error:", error);
    },
  });

  const { mutate: featuredChangeHandler, isPending: featuredChangeInPending } = useMutation({
    mutationFn: async ({ id, isFeatured }) => {
      queryClient.setQueryData(["post-categories", filters, search, selectFilterItem], (state) => {
        if (!state) return;

        const updatedData = state?.data?.map((item) =>
          item?._id === id
            ? {
                ...item,
                isFeatured: item?.isFeatured === false ? true : false,
              }
            : item
        );

        return { ...state, data: updatedData };
      });
      const res = await taxonomyStatusChange({ ids: [id], isFeatured });

      return { res, payload: { id, isFeatured } };
    },

    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        toast.success("Category status change successfully.");
      } else {
        queryClient.setQueryData(["post-categories", filters, search, selectFilterItem], (state) => {
          if (!state) return;

          const revertedData = state?.data?.map((item) =>
            item?._id === payload.id
              ? {
                  ...item,
                  isFeatured: item?.isFeatured === false ? true : false,
                }
              : item
          );

          return { ...state, data: revertedData };
        });

        toast.error(res?.message || "Failed to update status.");
      }
    },

    onError: (error) => {
      toast.error(error?.message || "Something went wrong while updating status.");
      console.error("Status update error:", error);
    },
  });

  const tableHeadData = [
    {
      _id: 1,
      name: "Image",
      align: "left",
    },
    {
      _id: 2,
      name: "Title",
      align: "left",
      isSort: true,
      onSort: (val) => {
        if (val === "DESC") {
          setFilters((pre) => ({ ...pre, sortQuery: "-content.en.title" }));
        } else {
          setFilters((pre) => ({ ...pre, sortQuery: "content.en.title" }));
        }
      },
    },

    // {
    //   _id: 3,
    //   name: "Created At",
    //   align: "left",
    //   isSort: true,
    //   onSort: (val) => {
    //     if (val === "DESC") {
    //       setFilters((pre) => ({ ...pre, sortQuery: "-createdAt" }));
    //     } else {
    //       setFilters((pre) => ({ ...pre, sortQuery: "createdAt" }));
    //     }
    //   },
    // },

    {
      _id: 4,
      name: "Status",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            status: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.status;
            return { ...pre };
          });
        }
      },
      filterData: status_filter || [],
    },
    // {
    //   _id: 5,
    //   name: "Featured",
    //   align: "left",
    //   isFilter: true,
    //   isFilterSearch: false,
    //   onFilter: (val) => {
    //     if (val?.length > 0 && val?.length === 1) {
    //       setFilters((pre) => ({
    //         ...pre,
    //         isFeatured: val?.map((item) => item?.value),
    //         page: 1,
    //       }));
    //     } else {
    //       setFilters((pre) => {
    //         delete pre?.isFeatured;
    //         return { ...pre };
    //       });
    //     }
    //   },
    //   filterData: featured_filter || [],
    // },
    {
      _id: 6,
      name: "Action",
      align: "left",
    },
  ];

  const tagColors = [
    "bg-purple-100 text-purple-700 border border-purple-200",
    "bg-lime-100 text-lime-700 border border-lime-200",
    "bg-orange-100 text-orange-700 border border-orange-200",
    "bg-blue-100 text-blue-700 border border-blue-200",
    "bg-purple-100 text-purple-700 border border-purple-200",
    "bg-orange-100 text-orange-700 border border-orange-200",
    "bg-pink-100 text-pink-700 border border-pink-200",
    "bg-indigo-100 text-indigo-700 border border-indigo-200",
    "bg-teal-100 text-teal-700 border border-teal-200",
    "bg-yellow-100 text-yellow-700 border border-yellow-200",
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "image",
          align: "left",
          cellClass: "!min-w-[100px]",
          image: item?.mediaGallery?.[0]?.url,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.content?.[userLang]?.title || "-",
        },

        // {
        //   _id: 3,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: item?.createdAt ? dayjs(item?.createdAt).format("DD MMM, YYYY") : "-",
        // },

        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div
              className="flex gap-2 items-center cursor-pointer"
              onClick={() => {
                statusChangeHandler({
                  id: item?._id,
                  status: item?.status === "active" ? "inactive" : "active",
                });
              }}>
              <Badge
                type={item?.status === "active" ? "success" : "danger"}
                label={item?.status === "active" ? "Active" : "Inactive"}
              />
            </div>
          ),
        },
        // {
        //   _id: 5,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: (
        //     <div
        //       className="flex gap-2 items-center cursor-pointer"
        //       onClick={() => {
        //         featuredChangeHandler({
        //           id: item?._id,
        //           isFeatured: item?.isFeatured === false ? true : false,
        //         });
        //       }}>
        //       <Badge
        //         type={item?.isFeatured === true ? "info" : "warning"}
        //         label={item?.isFeatured === true ? "Yes" : "No"}
        //       />
        //     </div>
        //   ),
        // },
        {
          _id: 6,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Edit",
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
            },
            {
              _id: 4,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  // add or edit Response =================>
  async function taxonomyManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["blog-categories", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [...stateData, res?.data];
              return { ...state, data: stateData };
            }
          });
          await queryClient.refetchQueries(["blog-categories", filters, search]);
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }

      if (res?.status === 200) {
        queryClient.setQueryData(["blog-categories", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) => (item?._id === res?.data?._id ? { ...item, ...res?.data } : item));
            return { ...state, data: stateData };
          }
        });
        await queryClient.refetchQueries(["blog-categories", filters, search]);
      }

      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  // category delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await taxonomyDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        setSelectedItems([]);
        queryClient.setQueryData(["post-categories", filters, search, selectFilterItem], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter((item) => !payload?.includes(item?._id));
            return { ...state, data: stateData };
          }
        });

        queryClient.invalidateQueries(["post-categories", filters, search, selectFilterItem]);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const options = [
    {
      id: "blogCategoryDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "Blog Category Delete",
    },
  ];

  function actionHandler(val) {
    switch (val) {
      case "blogCategoryDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  return (
    <div>
      <div className="space-y-4 w-full flex flex-col pb-10">
        <div className="font-semibold text-lg xls:text-sm xl:text-3xl text-primary text-right xls:text-left">
          {`Menu Categories`}
        </div>
        <div className="space-y-8">
          {selectedAllFilters?.length > 0 && (
            <div className="flex gap-2 items-center flex-wrap mt-2">
              {selectedAllFilters?.map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium capitalize">
                  {item}
                  <button
                    onClick={() => {
                      setSelectedAllFilters((prev) => prev.filter((i) => i !== item));
                      setSelectedFilter(null);
                      setSelectFilterItem({});
                    }}
                    className="text-blue-600 hover:text-red-500 focus:outline-none">
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex mt-5 items-center justify-between flex-col xls:flex-row">
            <TableControls
              limit={filters?.limit}
              totalDocs={data?.metadata?.totalDocs}
              onLimitChange={(val) => handleFilterChange("limit", val, setFilters)}
              onSearch={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
            {/* <TagFilter
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              setSelectFilterItem={setSelectFilterItem}
              setSelectedAllFilters={setSelectedAllFilters}
              filterOptions={[
                { name: "Status", value: "status" },
                { name: "Featured", value: "featured" },
              ]}
            /> */}
            {selectedItems?.length > 0 && (
              <div className="flex items-center gap-2">
                <ActionBar options={options} onChange={actionHandler} />
                <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                  {selectedItems?.length} Selected
                  <button onClick={() => setSelectedItems([])} type="button" className="cursor-pointer">
                    <i className="fa-fw fa-light fa-xmark"></i>
                  </button>
                </span>
              </div>
            )}
            <div className="relative xls:ml-auto flex-shrink-0 mt-3 xls:mt-0">
              <Button
                buttonClasses={"hover:bg-emerald-700"}
                buttonLabel={"Add Category"}
                buttonIcon={"fa-regular fa-plus"}
                buttonIconPosition="left"
                buttonLabelClasses={"font-medium !text-sm"}
                buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
              />
            </div>
          </div>
        </div>

        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          totalDataCount={data?.metadata?.totalDocs}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          handleOrdering={(val) => {
            console.log(val);
          }}
          isFetching={isFetching}
          isOrderingDisabled={true}
          selectable
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
          showingData={getPaginationRange(filters?.page, filters?.limit, data?.metadata?.totalDocs)}
        />

        <CategoryMangeDrawer
          open={modalOpen?.isOpen}
          data={modalOpen?.data}
          close={() => setModalOpen({ isOpen: false, data: null })}
          getResponse={taxonomyManageResponse}
          type={"product-category"}
          title={"Menu"}
        />
        <DeleteModal
          open={deleteModal?.isOpen}
          loading={deletePending}
          onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
          deleteHandler={() => {
            deleteHandler(deleteModal?.data?._id);
          }}
        />
      </div>
    </div>
  );
};

export default MenuCategory;
